import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  TrendingUp,
  Landmark,
  Scale,
  ArrowRight,
  ArrowUpRight,
  Building2,
  Clock,
  FileText,
  AlertTriangle,
  CheckCircle2,
  BarChart3,
  Globe,
  Sparkles,
  ExternalLink,
  ChevronDown,
  Layers,
  Percent,
  DollarSign,
  Award,
  Zap,
  Check,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Chf from "@/components/Chf";
import NavbarHeader from "@/components/NavbarHeader";
import PartnerLoginModal from "@/components/PartnerLoginModal";
import SingaporeLaunchMap from "@/components/SingaporeLaunchMap";
import PrimeVsNonPrimeBars from "@/components/PrimeVsNonPrimeBars";
import SectionNavScroller from "@/components/SectionNavScroller";

const ADVANTAGE_SECTIONS = [
  { id: "overview", num: "00", label: "Overview", subtitle: "Four Core Reasons" },
  { id: "singapore-market", num: "01", label: "Macro Allocation", subtitle: "Dual-Hub Strategy" },
  { id: "why-residential", num: "02", label: "The Asset Class", subtitle: "Launch Absorption" },
  { id: "risks-resilience", num: "03", label: "Risk & Resilience", subtitle: "7-Crisis Track Record" },
  { id: "contact", num: "04", label: "Advisory Mandate", subtitle: "Private Desk" },
];

const URA_PPI_DATA = [
  { year: 1996, index: 89.6 },
  { year: 1997, index: 80.4 },
  { year: 1998, index: 52.1 },
  { year: 1999, index: 68.3 },
  { year: 2000, index: 72.8 },
  { year: 2001, index: 64.9 },
  { year: 2002, index: 62.5 },
  { year: 2003, index: 58.7 },
  { year: 2004, index: 63.8 },
  { year: 2005, index: 69.2 },
  { year: 2006, index: 76.3 },
  { year: 2007, index: 100.2 },
  { year: 2008, index: 98.4 },
  { year: 2009, index: 88.5 },
  { year: 2010, index: 118.2 },
  { year: 2011, index: 128.5 },
  { year: 2012, index: 137.9 },
  { year: 2013, index: 154.6 },
  { year: 2014, index: 150.2 },
  { year: 2015, index: 144.7 },
  { year: 2016, index: 140.2 },
  { year: 2017, index: 144.5 },
  { year: 2018, index: 156.2 },
  { year: 2019, index: 159.5 },
  { year: 2020, index: 162.8 },
  { year: 2021, index: 180.1 },
  { year: 2022, index: 195.6 },
  { year: 2023, index: 208.9 },
  { year: 2024, index: 217.1 },
  { year: 2025, index: 224.5 },
];

const HISTORICAL_CRISES = [
  {
    id: "afc",
    num: "01",
    year: "1997 – 1998",
    name: "Asian Financial Crisis",
    short: "1997 Asian Crisis",
    tag: "Rebounded in 6 Quarters",
    indexVal: "Index 52.1",
    impact: "Regional currency collapse (Baht, Rupiah, Ringgit) triggered sharp regional liquidity freeze.",
    action: "MAS defended SGD liquidity; guided banking fortress consolidations to maintain bank stability.",
    recovery: "Sharp V-shaped recovery in 6 quarters, stabilizing private values into 2000.",
    x: 122.1,
    y: 331.9,
    labelY: 260,
  },
  {
    id: "sars",
    num: "02",
    year: "2003",
    name: "SARS Epidemic & US-Iraq War",
    short: "2003 SARS & Iraq War",
    tag: "V-Shaped: 9 Mos (+9.5% GDP)",
    indexVal: "Index 58.7",
    impact: "Sharp travel, hotel, and retail freeze paired with Middle East oil volatility.",
    action: "Decisive clinical containment protocols and immediate fiscal relief for business owners.",
    recovery: "Fastest recovery in ASEAN (+9.5% GDP in 9 months); home prices doubled over next 4 years.",
    x: 277.2,
    y: 321.9,
    labelY: 250,
  },
  {
    id: "gfc",
    num: "03",
    year: "2008 – 2009",
    name: "Global Financial Crisis",
    short: "2008-09 GFC",
    tag: "Full Rebound in 7 Quarters",
    indexVal: "Index 88.5",
    impact: "Wall Street Lehman collapse & global banking liquidity freeze.",
    action: "S$20.5B Resilience Package drawn directly from sovereign reserves; zero banks failed or needed bailouts.",
    recovery: "Prices rebounded to all-time highs within 7 quarters (+38% price surge by 2010).",
    x: 463.1,
    y: 277.3,
    labelY: 215,
  },
  {
    id: "tdsr",
    num: "04",
    year: "2013",
    name: "TDSR Structural Framework",
    short: "2013 TDSR Shield",
    tag: "Zero Foreclosure Cascade",
    indexVal: "Index 154.6",
    impact: "Post-QE global cheap money threatened speculative domestic asset bubbles.",
    action: "MAS instituted mandatory 55% Total Debt Servicing Ratio (TDSR) income cap on all property mortgages.",
    recovery: "Engineered a controlled, plateaued soft-landing with zero subprime contagion or bad debt.",
    x: 587.2,
    y: 178.1,
    labelY: 125,
  },
  {
    id: "tariffs",
    num: "05",
    year: "2018",
    name: "US-China Trade War & Tariffs",
    short: "2018 Trade War",
    tag: "Record Family Office Inflow",
    indexVal: "Index 156.2",
    impact: "Global protectionism, tariff escalations, and cross-border supply chain relocations.",
    action: "Singapore positioned as the neutral, trusted rule-of-law capital sanctuary between East and West.",
    recovery: "Global wealth inflow surged; over 1,400 single-family offices established headquarters in Singapore.",
    x: 742.4,
    y: 175.7,
    labelY: 220,
  },
  {
    id: "covid",
    num: "06",
    year: "2020 – 2021",
    name: "COVID-19 Global Pandemic",
    short: "2020 COVID Pandemic",
    tag: "Prices Rose +10.6%",
    indexVal: "Index 162.8",
    impact: "Worldwide border shutdowns, economic circuit breaker, and total supply halt.",
    action: "S$100B fiscal rescue budgets deployed directly from national reserves with zero external debt borrowed.",
    recovery: "Home prices jumped +10.6% during the crisis driven by flight-to-safety domestic & international capital.",
    x: 804.5,
    y: 165.8,
    labelY: 115,
  },
  {
    id: "ukraine",
    num: "07",
    year: "2022 – 2023",
    name: "Russia-Ukraine War & Fed Rate Hikes",
    short: "2022 Ukraine & Rates",
    tag: "+6.8% Gain Despite Global Dip",
    indexVal: "Index 195.6",
    impact: "Worst global inflation shock in 40 years alongside unprecedented 500bps US Fed interest rate spikes.",
    action: "MAS delivered consecutive monetary tightening rounds, fortifying the SGD NEER against imported inflation.",
    recovery: "Singapore prices defied global residential downturns, compounding +6.8% higher into 2023.",
    x: 866.6,
    y: 116.6,
    labelY: 65,
  },
  {
    id: "absd60",
    num: "08",
    year: "2024 – 2026",
    name: "60% ABSD Regime & Record Peak",
    short: "2024-25 All-Time Peak",
    tag: "8th Consecutive Year of Growth",
    indexVal: "Index 224.5",
    impact: "General foreign stamp duty doubled to 60%, weeding out short-term speculative flippers.",
    action: "Swiss and FTA treaty nationals legally protected under EFTA Article 10.5 with 0% ABSD treatment.",
    recovery: "Private home prices hit historic high of 224.5 — marking 8 consecutive years of uninterrupted compounding.",
    x: 960.0,
    y: 73.3,
    labelY: 35,
  },
];

export default function Advantage() {
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [activeCrisisIndex, setActiveCrisisIndex] = useState(2); // Default to 2008 GFC

  useEffect(() => {
    document.title = "The Singapore Advantage | 0% ABSD, High Returns & Crisis Resilience | Alpine Straits";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F3EE] text-[#0A1128] selection:bg-[#C5A880]/30 font-sans">
      <PartnerLoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />

      {/* Shared Navigation Header */}
      <NavbarHeader />

      {/* Floating Scroll Nav Indicator */}
      <SectionNavScroller sections={ADVANTAGE_SECTIONS} />

      {/* ==================================================================== */}
      {/* HERO SECTION: BIG VISUAL HOOKS & KEY METRICS                         */}
      {/* ==================================================================== */}
      <section id="overview" className="relative border-b border-[#E7E2D6] bg-gradient-to-b from-[#F5F3EE] via-[#EDE9E0] to-[#F5F3EE] pt-14 pb-20 px-6 lg:px-12 overflow-hidden">
        {/* Subtle dot pattern */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(#0A1128 1.5px, transparent 1.5px)",
            backgroundSize: "28px 28px",
          }}
        />

        <div className="max-w-7xl mx-auto relative">
          {/* Eyebrow Label */}
          <div className="flex flex-wrap items-center justify-between gap-3 pb-6 border-b border-[#E7E2D6]/80 text-xs font-mono">
            <span className="inline-flex items-center gap-2 bg-[#0A1128] text-[#C5A880] px-3 py-1 rounded-full text-[11px] font-semibold tracking-wider uppercase">
              <Sparkles className="w-3.5 h-3.5" />
              Strategic Wealth Dossier
            </span>
            <span className="text-slate-500">Alpine Straits Property Partners · Singapore &amp; Zurich</span>
          </div>

          {/* Big Editorial Headline */}
          <div className="mt-10 max-w-4xl">
            <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl text-[#0A1128] font-medium tracking-tight leading-[1.05]">
              Why Singapore. <br />
              <span className="text-[#C5A880] italic">The Unrivaled Real Estate Advantage.</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-slate-600 leading-relaxed max-w-3xl">
              Enjoy <strong className="text-[#0A1128] font-semibold">0% Foreign Buyer ABSD</strong> under the Swiss–Singapore Treaty, and secure capital in the world&apos;s most resilient safe-haven market.
            </p>
          </div>

          {/* WHY SINGAPORE: FOUR REASONS, WRITTEN LIKE A CONVERSATION, NOT A REPORT */}
          <div className="mt-14 bg-white border border-[#E7E2D6] rounded-3xl p-6 sm:p-10 lg:p-14 shadow-xs relative overflow-hidden">
            {/* Intro */}
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 pb-10 mb-2 border-b border-[#E7E2D6]">
              <div className="lg:col-span-5">
                <p className="text-sm italic font-serif-display text-[#C5A880]">
                  The short version
                </p>
                <h3 className="font-serif-display text-3xl sm:text-4xl text-[#0A1128] font-medium tracking-tight leading-[1.1] mt-2">
                  Four reasons our Swiss clients keep choosing Singapore.
                </h3>
              </div>
              <div className="lg:col-span-7 lg:pt-8">
                <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                  When clients first call us from Zurich or Geneva, the questions are almost always the same: <em className="text-[#0A1128]">Is the tax break real? How much do I need to put down? Can I get out if I need to? And what happens if things go wrong?</em> Here are the honest answers.
                </p>
              </div>
            </div>

            {/* The four reasons */}
            <div className="grid md:grid-cols-2">
              {/* 01 — The treaty */}
              <article className="py-10 md:pr-10 lg:pr-14 border-b border-[#E7E2D6] md:border-r">
                <div className="flex items-baseline gap-4">
                  <span className="font-serif-display italic text-5xl text-[#C5A880] leading-none">1</span>
                  <h4 className="font-serif-display text-2xl text-[#0A1128] font-medium">
                    You&apos;re treated like a local.
                  </h4>
                </div>
                <p className="mt-4 text-slate-600 leading-relaxed">
                  Most foreigners pay a 60% stamp duty before they even get the keys. Under the Singapore–EFTA free trade agreement, Swiss citizens buying their first home pay what Singaporeans pay: <strong className="text-[#0A1128] font-semibold">nothing extra</strong>.
                </p>

                <div className="mt-6 space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-slate-500">Most foreign buyers</span>
                      <span className="text-[#0A1128] font-semibold">60%</span>
                    </div>
                    <div className="w-full bg-[#F1ECE2] h-2.5 rounded-full overflow-hidden">
                      <div className="bg-[#0A1128] h-full w-full rounded-full" />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-emerald-700 font-medium">You, as a Swiss citizen</span>
                      <span className="text-emerald-700 font-semibold">0%</span>
                    </div>
                    <div className="w-full bg-[#F1ECE2] h-2.5 rounded-full overflow-hidden">
                      <div className="bg-emerald-500 h-full w-[2%] rounded-full" />
                    </div>
                  </div>
                </div>

                <p className="mt-6 font-serif-display italic text-lg text-[#0A1128]">
                  On a <Chf sgd={5000000} compact /> home, that&apos;s roughly <Chf sgd={3000000} compact /> that stays in your pocket.
                </p>
              </article>

              {/* 02 — Staged payments */}
              <article className="py-10 md:pl-10 lg:pl-14 border-b border-[#E7E2D6]">
                <div className="flex items-baseline gap-4">
                  <span className="font-serif-display italic text-5xl text-[#C5A880] leading-none">2</span>
                  <h4 className="font-serif-display text-2xl text-[#0A1128] font-medium">
                    You pay as it&apos;s built.
                  </h4>
                </div>
                <p className="mt-4 text-slate-600 leading-relaxed">
                  Buy a new launch and you don&apos;t hand over everything on day one. Roughly 20% goes in at signing; the rest is drawn down as the foundation, structure and finishings go up over three to four years.
                </p>

                {/* Simple timeline */}
                <div className="mt-6 relative">
                  <div className="absolute left-0 right-0 top-[7px] h-px bg-[#E7E2D6]" />
                  <div className="relative grid grid-cols-4 gap-2 text-center">
                    {[
                      { pct: "20%", label: "At signing", dot: "bg-[#0A1128]" },
                      { pct: "~40%", label: "During build", dot: "bg-[#C5A880]" },
                      { pct: "25%", label: "Keys (TOP)", dot: "bg-[#C5A880]" },
                      { pct: "15%", label: "Completion", dot: "bg-emerald-600" },
                    ].map((s) => (
                      <div key={s.label} className="flex flex-col items-center">
                        <span className={`w-3.5 h-3.5 rounded-full ${s.dot} ring-4 ring-white`} />
                        <span className="mt-3 font-serif-display text-xl text-[#0A1128]">{s.pct}</span>
                        <span className="text-xs text-slate-500 mt-0.5">{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <p className="mt-6 font-serif-display italic text-lg text-[#0A1128]">
                  Your capital keeps working elsewhere while the building rises.
                </p>
              </article>

              {/* 03 — Exit liquidity */}
              <article className="py-10 md:pr-10 lg:pr-14 border-b md:border-b-0 border-[#E7E2D6] md:border-r">
                <div className="flex items-baseline gap-4">
                  <span className="font-serif-display italic text-5xl text-[#C5A880] leading-none">3</span>
                  <h4 className="font-serif-display text-2xl text-[#0A1128] font-medium">
                    There&apos;s always someone to sell to.
                  </h4>
                </div>
                <p className="mt-4 text-slate-600 leading-relaxed">
                  Singapore has a deep pool of buyers, from upgrading local families to regional wealth. In the prime districts, the sub-sale deals we tracked closed with average gains of <strong className="text-[#0A1128] font-semibold"><Chf sgd={430000} compact /> to <Chf sgd={786000} compact /></strong>. In the suburbs, the gains are smaller but the buyers queue up faster.
                </p>

                <div className="mt-6 grid grid-cols-2 gap-3">
                  <div className="rounded-xl bg-[#FAF8F5] border border-[#E7E2D6] p-4">
                    <div className="text-xs text-slate-500">Prime (CCR)</div>
                    <div className="font-serif-display text-lg text-[#0A1128] mt-1">Bigger gains</div>
                  </div>
                  <div className="rounded-xl bg-[#FAF8F5] border border-[#E7E2D6] p-4">
                    <div className="text-xs text-slate-500">Suburban hubs</div>
                    <div className="font-serif-display text-lg text-[#0A1128] mt-1">Faster sales</div>
                  </div>
                </div>

                <p className="mt-6 font-serif-display italic text-lg text-[#0A1128]">
                  Some owners resell before the building is even finished.
                </p>
              </article>

              {/* 04 — Stability */}
              <article className="py-10 md:pl-10 lg:pl-14">
                <div className="flex items-baseline gap-4">
                  <span className="font-serif-display italic text-5xl text-[#C5A880] leading-none">4</span>
                  <h4 className="font-serif-display text-2xl text-[#0A1128] font-medium">
                    It&apos;s built not to break.
                  </h4>
                </div>
                <p className="mt-4 text-slate-600 leading-relaxed">
                  Think of it as the Switzerland of Asia. The state holds over S$1 trillion in reserves, carries no net external debt, and caps how much any household can borrow. That&apos;s why Singapore never had a subprime crisis.
                </p>

                <dl className="mt-6 divide-y divide-[#E7E2D6] border-y border-[#E7E2D6]">
                  <div className="flex justify-between py-2.5 text-sm">
                    <dt className="text-slate-500">National reserves</dt>
                    <dd className="text-[#0A1128] font-semibold">Over S$1 trillion</dd>
                  </div>
                  <div className="flex justify-between py-2.5 text-sm">
                    <dt className="text-slate-500">Net external debt</dt>
                    <dd className="text-emerald-700 font-semibold">None</dd>
                  </div>
                  <div className="flex justify-between py-2.5 text-sm">
                    <dt className="text-slate-500">Max. income spent on debt</dt>
                    <dd className="text-[#0A1128] font-semibold">55%</dd>
                  </div>
                </dl>

                <p className="mt-6 font-serif-display italic text-lg text-[#0A1128]">
                  Quietly boring, in the best possible way.
                </p>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* ==================================================================== */}
      {/* SECTION 1: DUAL-HUB STRATEGY // ZURICH & SINGAPORE                   */}
      {/* ==================================================================== */}
      <section id="singapore-market" className="py-20 px-6 lg:px-12 max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <div className="label-eyebrow text-[#C5A880] font-mono mb-2">01 // The Macro Allocation</div>
            <h2 className="font-serif-display text-3xl sm:text-5xl text-[#0A1128] font-medium tracking-tight">
              Why Singapore? The Dual-Hub Strategy.
            </h2>
            <p className="font-serif-display text-xl sm:text-2xl text-[#C5A880] italic mt-2">
              Hold liquidity in Zurich. Compound physical real estate in Asia&apos;s premier AAA fortress.
            </p>
          </div>
          <div className="bg-white px-4 py-2 rounded-xl border border-[#E7E2D6] text-xs font-semibold text-[#0A1128] shadow-xs flex items-center gap-2">
            <Award className="w-4 h-4 text-[#C5A880]" />
            AAA Sovereign Credit · Top 9 Nations in the World
          </div>
        </div>

        {/* 1. DUAL-HUB WEALTH PRESERVATION & TAX NEUTRALITY DOSSIER */}
        <div className="bg-white border border-[#E7E2D6] rounded-3xl p-8 lg:p-12 shadow-xs mb-16">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            {/* Left Column: Strategic Rationale */}
            <div className="lg:col-span-6 space-y-5">
              <span className="text-[11px] font-mono uppercase tracking-widest text-[#C5A880] font-bold">
                Geographic Diversification // Zurich &amp; Singapore
              </span>
              <h3 className="font-serif-display text-2xl sm:text-3xl text-[#0A1128] font-medium leading-tight">
                Two safe havens on opposite sides of the globe.
              </h3>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                Most Swiss families we advise already hold private banking relationships in Zurich or Geneva. But true wealth preservation isn&apos;t just about holding different currencies in the same European time zone—it&apos;s about having a physical foothold in an independent, politically neutral, English-law jurisdiction.
              </p>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                Singapore is essentially the Switzerland of Asia: strictly non-aligned, backed by over <strong className="text-[#0A1128]">S$1 trillion</strong> in sovereign reserves, zero net external debt, and an exchange rate (<strong className="text-[#0A1128]">S$NEER</strong>) managed by MAS to appreciate steadily against global currencies, mirroring the discipline of the Swiss Franc.
              </p>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                Crucially, every square meter of private residential property is protected under the <strong className="text-[#0A1128]">Torrens Title register</strong>—meaning your title is indefeasible and government-guaranteed, backed by English common law with zero political expropriation risk.
              </p>
              
              <div className="pt-2 flex flex-wrap gap-2 text-[11px] font-mono text-slate-500">
                <span className="bg-[#FAF8F5] px-2.5 py-1 rounded-md border border-[#E7E2D6]">Torrens Title Guarantee</span>
                <span className="bg-[#FAF8F5] px-2.5 py-1 rounded-md border border-[#E7E2D6]">English Common Law</span>
                <span className="bg-[#FAF8F5] px-2.5 py-1 rounded-md border border-[#E7E2D6]">Zero Capital Controls</span>
              </div>
            </div>

            {/* Right Column: Tax & Wealth Preservation Scorecard */}
            <div className="lg:col-span-6">
              <div className="bg-[#FAF8F5] border border-[#E7E2D6] rounded-2xl p-6 sm:p-7 space-y-4">
                <div className="flex items-center justify-between pb-3 border-b border-[#E7E2D6]">
                  <div>
                    <div className="text-xs font-mono uppercase tracking-wider text-slate-500 font-semibold">
                      Tax &amp; Wealth Preservation Scorecard
                    </div>
                    <div className="font-serif-display text-lg text-[#0A1128] font-medium mt-0.5">
                      Singapore vs. Traditional Real Estate Hubs
                    </div>
                  </div>
                  <span className="text-[10px] font-mono bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full font-semibold">
                    Triple Zero
                  </span>
                </div>

                {/* Scorecard Rows */}
                <div className="space-y-3 pt-1">
                  {/* Capital Gains */}
                  <div className="bg-white rounded-xl p-3.5 border border-[#E7E2D6] shadow-2xs">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-800">Capital Gains Tax on Property</span>
                      <span className="font-serif-display text-base font-bold text-emerald-700">0% (Tax-Free)</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-normal">
                      Switzerland levies up to 40% (cantonal <em>Grundstückgewinnsteuer</em>); UK up to 24%; US up to 20%+. In Singapore, 100% of your real estate capital appreciation is preserved.
                    </p>
                  </div>

                  {/* Inheritance / Estate Tax */}
                  <div className="bg-white rounded-xl p-3.5 border border-[#E7E2D6] shadow-2xs">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-800">Estate &amp; Inheritance Tax</span>
                      <span className="font-serif-display text-base font-bold text-emerald-700">0% (Abolished)</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-normal">
                      The UK levies 40% above £325k; the US taxes foreign estates at 40% above $60k. Singapore abolished death tax entirely in 2008, ensuring frictionless generational wealth transfer.
                    </p>
                  </div>

                  {/* Annual Wealth Tax */}
                  <div className="bg-white rounded-xl p-3.5 border border-[#E7E2D6] shadow-2xs">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-800">Annual Net Wealth Tax</span>
                      <span className="font-serif-display text-base font-bold text-emerald-700">None (0%)</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-normal">
                      Unlike the Swiss <em>Vermögenssteuer</em> (which taxes your worldwide net equity annually), Singapore levies no tax simply for holding real estate.
                    </p>
                  </div>

                  {/* Capital Mobility */}
                  <div className="bg-white rounded-xl p-3.5 border border-[#E7E2D6] shadow-2xs">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-slate-800">Capital Mobility &amp; FX Freedom</span>
                      <span className="font-serif-display text-base font-bold text-[#0A1128]">100% Free Repatriation</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1 leading-normal">
                      Zero currency or exchange controls. Convert and remit rental yields or sale proceeds freely to Swiss bank accounts in CHF, USD, or EUR without restriction.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Pillar Strip */}
          <div className="mt-10 pt-8 border-t border-[#E7E2D6] grid grid-cols-2 md:grid-cols-4 gap-6 text-left">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-mono">Capital Gains Tax</div>
              <div className="font-serif-display text-2xl text-[#0A1128] font-semibold mt-1">0% (Zero)</div>
              <p className="text-xs text-slate-500 mt-0.5">Keep 100% of physical asset appreciation</p>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-mono">Inheritance Tax</div>
              <div className="font-serif-display text-2xl text-[#0A1128] font-semibold mt-1">0% (Zero)</div>
              <p className="text-xs text-slate-500 mt-0.5">Frictionless generational wealth succession</p>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-mono">Monetary Policy</div>
              <div className="font-serif-display text-2xl text-[#0A1128] font-semibold mt-1">S$NEER Basket</div>
              <p className="text-xs text-slate-500 mt-0.5">Trade-weighted defense against imported inflation</p>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 font-mono">Land Scarcity</div>
              <div className="font-serif-display text-2xl text-[#0A1128] font-semibold mt-1">735 km² Island</div>
              <p className="text-xs text-slate-500 mt-0.5">Strict supply quotas with no suburban expansion</p>
            </div>
          </div>
        </div>

        {/* 2. RECENT NEWS CLIPPINGS WITH ORIGINAL THUMBNAILS (POST-2023) */}
        <div>
          <div className="flex items-center justify-between mb-8 pb-3 border-b border-[#E7E2D6]">
            <div>
              <div className="text-xs font-mono text-[#C5A880] uppercase tracking-wider font-semibold">
                Press Coverage &amp; Market Evidence
              </div>
              <h3 className="font-serif-display text-2xl sm:text-3xl text-[#0A1128] font-medium mt-1">
                Recent News Intelligence
              </h3>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                source: "The Business Times",
                shortSource: "Business Times",
                publisher: "Singapore Press Holdings",
                date: "Jan 2025",
                tag: "8 Years Consecutive Growth",
                title: "“Singapore’s Private Home Prices Swing to 2.3% in Q4, Taking 2024 Gain to 3.9%”",
                summary: "Private home values steadily compound higher despite elevated global interest rates, anchored by resilient domestic wealth and new launch sales volume.",
                image: `${process.env.PUBLIC_URL || ""}/images/news/business-times-hero.jpg`,
                imageFit: "object-cover object-center",
                url: "https://www.businesstimes.com.sg/property/singapores-private-home-prices-swing-2-3-q4-strong-new-launch-sales-taking-2024-gain-3-9",
              },
              {
                source: "Channel News Asia",
                shortSource: "CNA",
                publisher: "Mediacorp Singapore",
                date: "Apr 2023",
                tag: "Treaty Buyer Edge",
                title: "“Singapore Doubles Property Tax for Foreign Buyers to Global High of 60%”",
                summary: "The 60% foreign stamp duty weeded out speculative flipping, giving Swiss and treaty buyers with 0% ABSD a massive, unrivaled capital advantage.",
                image: `${process.env.PUBLIC_URL || ""}/images/news/bloomberg-hero.jpg`,
                imageFit: "object-cover object-center",
                url: "https://www.channelnewsasia.com/singapore/property-cooling-measures-absd-foreigner-tax-double-singapore-buyers-5588321",
              },
              {
                source: "Morgan Stanley",
                shortSource: "Morgan Stanley",
                publisher: "Morgan Stanley Research",
                date: "Mid-2024",
                tag: "Top Defensive Asset",
                title: "“Singapore’s Economic Growth Ushers in New Era of Wealth”",
                summary: "Research highlights Singapore's transition into a sovereign hub for wealth preservation, private capital stability, and resilient real estate demand.",
                image: `${process.env.PUBLIC_URL || ""}/images/news/morgan-stanley-hero.jpg`,
                imageFit: "object-cover object-center",
                url: "https://www.morganstanley.com/insights/articles/singapore-economic-growth-new-wealth-era",
              },
              {
                source: "IRAS / Ministry of Finance",
                shortSource: "IRAS",
                publisher: "Inland Revenue Authority of Singapore",
                date: "Official Circular",
                tag: "Statutory 0% ABSD Remission",
                title: "“Foreigners Eligible for ABSD Remission Under Free Trade Agreements (FTAs)”",
                summary: "Official statutory guidelines confirming Swiss nationals receive Singapore Citizen tax treatment (0% ABSD on first residential property).",
                image: `${process.env.PUBLIC_URL || ""}/images/news/iras-fta-remission.png`,
                imageFit: "object-cover object-top",
                url: "https://www.iras.gov.sg/taxes/stamp-duty/for-property/appeals-refunds-reliefs-and-remissions/common-stamp-duty-remissions-and-reliefs-for-property/foreigners-eligible-for-absd-remission-under-free-trade-agreements-(ftas)",
              },
            ].map((article, idx) => (
              <a
                key={idx}
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                title={`Read full coverage on ${article.source}`}
                className="bg-white border border-[#E7E2D6] rounded-2xl overflow-hidden shadow-xs hover:shadow-xl hover:border-[#C5A880] transition-all flex flex-col group cursor-pointer block text-left"
              >
                {/* Media Image Banner */}
                <div className="h-44 overflow-hidden relative bg-slate-100">
                  <img
                    src={article.image}
                    alt={`${article.source} coverage thumbnail`}
                    className={`w-full h-full ${article.imageFit || "object-cover"} group-hover:scale-105 transition-transform duration-500`}
                  />
                  {/* Floating Source Tag */}
                  <div className="absolute top-3 left-3 bg-[#0A1128]/90 backdrop-blur px-2.5 py-1 rounded text-[10px] font-mono font-semibold text-[#C5A880]">
                    {article.source} · {article.date}
                  </div>
                  <div className="absolute top-3 right-3 bg-white/95 backdrop-blur p-1 rounded-md text-[#0A1128] opacity-80 group-hover:opacity-100 group-hover:text-[#C5A880] transition-all shadow-xs">
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </div>
                </div>

                <div className="p-5 flex-1 flex flex-col justify-between">
                  <div>
                    {/* Clear Source Attribution */}
                    <div className="flex items-center gap-1.5 text-[11px] font-mono font-semibold text-[#C5A880] mb-2 uppercase tracking-wide">
                      <span className="text-slate-400 font-normal">Source:</span>
                      <span className="text-[#0A1128] font-bold">{article.source}</span>
                    </div>

                    <h4 className="font-serif-display text-base font-semibold text-[#0A1128] group-hover:text-[#C5A880] transition-colors leading-snug">
                      {article.title}
                    </h4>
                    <p className="mt-2 text-xs text-slate-600 line-clamp-3 leading-relaxed">
                      {article.summary}
                    </p>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 text-[11px] font-semibold text-[#0A1128] flex items-center justify-between">
                    <span className="text-slate-500 font-mono text-[10px]">{article.tag}</span>
                    <span className="inline-flex items-center gap-1 text-[#C5A880] group-hover:text-[#0A1128] font-bold transition-colors">
                      Read on {article.shortSource || article.source} <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ==================================================================== */}
      {/* SECTION 2: WHY RESIDENTIAL? LAUNCH VELOCITY & SUB-SALE PROFITS       */}
      {/* ==================================================================== */}
      <section id="why-residential" className="py-20 px-6 lg:px-12 bg-white border-t border-[#E7E2D6]">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="max-w-3xl mb-12">
            <div className="label-eyebrow text-[#C5A880] font-mono mb-2">02 // The Asset Class</div>
            <h2 className="font-serif-display text-3xl sm:text-5xl text-[#0A1128] font-medium tracking-tight">
              Why Residential?
            </h2>
            <p className="font-serif-display text-xl sm:text-2xl text-[#C5A880] italic mt-2">
              Unprecedented Demand: 75%–99% Sold on Launch Weekend &amp; Exit Liquidity.
            </p>
          </div>

          {/* 1. VISUAL NEW LAUNCH ABSORPTION SPEEDOMETERS & MAP CALLOUTS */}
          <SingaporeLaunchMap />

          {/* 2. SIMPLIFIED EMPIRICAL SUB-SALE PERFORMANCE: VERTICAL BAR GRAPHS */}
          <PrimeVsNonPrimeBars />
        </div>
      </section>

      {/* ==================================================================== */}
      {/* SECTION 3: RISKS & MACRO RESILIENCE (THE 7-CRISIS TRACK RECORD)      */}
      {/* ==================================================================== */}
      <section id="risks-resilience" className="py-20 px-6 lg:px-12 bg-[#F5F3EE] border-t border-[#E7E2D6]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-12">
            <div className="label-eyebrow text-[#C5A880] font-mono mb-2">03 // Global Risk &amp; Resilience</div>
            <h2 className="font-serif-display text-3xl sm:text-5xl text-[#0A1128] font-medium tracking-tight">
              What About the Risks?
            </h2>
            <p className="font-serif-display text-xl sm:text-2xl text-[#C5A880] italic mt-2">
              Singapore is interconnected to world shocks, but sovereign stability drives lightning-fast recovery.
            </p>
          </div>

          {/* 1. THE SOVEREIGN SHIELD FORTRESS METRICS STRIP */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-10">
            <div className="bg-white border border-[#E7E2D6] rounded-2xl p-5 shadow-2xs">
              <div className="text-[11px] font-mono text-slate-400 uppercase tracking-wider font-bold">
                Sovereign Balance Sheet
              </div>
              <div className="font-serif-display text-2xl sm:text-3xl font-bold text-[#0A1128] mt-1">
                0% External Debt
              </div>
              <p className="text-xs text-slate-500 mt-1">Zero sovereign credit or default vulnerability</p>
            </div>

            <div className="bg-white border border-[#E7E2D6] rounded-2xl p-5 shadow-2xs">
              <div className="text-[11px] font-mono text-[#C5A880] uppercase tracking-wider font-bold">
                National Reserve Buffer
              </div>
              <div className="font-serif-display text-2xl sm:text-3xl font-bold text-[#0A1128] mt-1">
                &gt;S$1 Trillion
              </div>
              <p className="text-xs text-slate-500 mt-1">Unmatched fiscal capacity without foreign borrowing</p>
            </div>

            <div className="bg-white border border-[#E7E2D6] rounded-2xl p-5 shadow-2xs">
              <div className="text-[11px] font-mono text-emerald-700 uppercase tracking-wider font-bold">
                Banking Solvency
              </div>
              <div className="font-serif-display text-2xl sm:text-3xl font-bold text-emerald-600 mt-1">
                0 Bank Failures
              </div>
              <p className="text-xs text-slate-500 mt-1">50+ years of flawless financial system stability</p>
            </div>

            <div className="bg-white border border-[#E7E2D6] rounded-2xl p-5 shadow-2xs">
              <div className="text-[11px] font-mono text-[#0A1128] uppercase tracking-wider font-bold">
                Prudential Safeguard
              </div>
              <div className="font-serif-display text-2xl sm:text-3xl font-bold text-[#0A1128] mt-1">
                55% TDSR Cap
              </div>
              <p className="text-xs text-slate-500 mt-1">Mandatory cash equity buffers prevent foreclosures</p>
            </div>
          </div>

          {/* 2. THE ANNOTATED RESIDENTIAL PROPERTY PRICE INDEX LINE CHART */}
          <div className="bg-white border border-[#E7E2D6] rounded-3xl p-6 sm:p-8 lg:p-10 shadow-sm">
            {/* Chart Header */}
            <div className="flex flex-col md:flex-row md:items-end justify-between pb-6 mb-6 border-b border-[#E7E2D6] gap-4">
              <div>
                <span className="text-xs font-mono uppercase text-[#C5A880] font-bold tracking-wider">
                  Empirical Macro Resilience · 1996 – 2025
                </span>
                <h3 className="font-serif-display text-2xl sm:text-3xl text-[#0A1128] font-bold mt-1">
                  Singapore Residential Property Price Index (PPI)
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  Official URA Private Residential Property Index. Demonstrates lightning-fast V-shaped rebounds through every major world crisis.
                </p>
              </div>

              <div className="flex items-center gap-4 text-xs font-mono shrink-0">
                <div className="flex items-center gap-2">
                  <span className="w-3.5 h-1 bg-[#0A1128] rounded-full inline-block" />
                  <span className="text-slate-600 font-semibold">URA PPI Curve</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#C5A880] inline-block ring-2 ring-[#0A1128]" />
                  <span className="text-[#C5A880] font-bold">Annotated World Shock</span>
                </div>
              </div>
            </div>

            {/* Responsive Interactive SVG Canvas */}
            <div className="w-full overflow-x-auto pb-4">
              <div className="min-w-[760px] lg:min-w-full">
                <svg
                  viewBox="0 0 1020 440"
                  className="w-full h-auto overflow-visible select-none"
                >
                  <defs>
                    <linearGradient id="ppiAreaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#C5A880" stopOpacity="0.32" />
                      <stop offset="50%" stopColor="#C5A880" stopOpacity="0.12" />
                      <stop offset="100%" stopColor="#C5A880" stopOpacity="0.01" />
                    </linearGradient>
                  </defs>

                  {/* Horizontal Gridlines & Index Scale */}
                  {[
                    { y: 73.3, label: "220" },
                    { y: 133.3, label: "180" },
                    { y: 193.3, label: "140" },
                    { y: 253.3, label: "100" },
                    { y: 313.3, label: "60" },
                  ].map((grid, i) => (
                    <g key={i}>
                      <line
                        x1="60"
                        y1={grid.y}
                        x2="960"
                        y2={grid.y}
                        stroke="#F1EFE9"
                        strokeWidth="1.2"
                        strokeDasharray="4 4"
                      />
                      <text
                        x="48"
                        y={grid.y}
                        textAnchor="end"
                        dominantBaseline="central"
                        fontSize="10"
                        fontFamily="monospace"
                        fill="#94A3B8"
                      >
                        {grid.label}
                      </text>
                    </g>
                  ))}

                  {/* Baseline Axis */}
                  <line x1="60" y1="350" x2="960" y2="350" stroke="#CBD5E1" strokeWidth="1.5" />

                  {/* X-Axis Years */}
                  {[
                    { year: "1996", x: 60 },
                    { year: "2000", x: 184 },
                    { year: "2005", x: 339 },
                    { year: "2010", x: 494 },
                    { year: "2015", x: 649 },
                    { year: "2020", x: 804 },
                    { year: "2025", x: 960 },
                  ].map((tick, i) => (
                    <g key={i}>
                      <line x1={tick.x} y1="350" x2={tick.x} y2="356" stroke="#94A3B8" strokeWidth="1.5" />
                      <text
                        x={tick.x}
                        y="372"
                        textAnchor="middle"
                        fontSize="11"
                        fontFamily="monospace"
                        fontWeight="600"
                        fill="#64748B"
                      >
                        {tick.year}
                      </text>
                    </g>
                  ))}

                  {/* Shaded Area Under Curve */}
                  <path
                    d={`${URA_PPI_DATA.map((p, i) => {
                      const x = +(60 + ((p.year - 1996) / 29) * 900).toFixed(1);
                      const y = +(350 - ((p.index - 40) / 200) * 300).toFixed(1);
                      return `${i === 0 ? "M" : "L"} ${x} ${y}`;
                    }).join(" ")} L 960 350 L 60 350 Z`}
                    fill="url(#ppiAreaGradient)"
                  />

                  {/* Main PPI Line Path */}
                  <path
                    d={URA_PPI_DATA.map((p, i) => {
                      const x = +(60 + ((p.year - 1996) / 29) * 900).toFixed(1);
                      const y = +(350 - ((p.index - 40) / 200) * 300).toFixed(1);
                      return `${i === 0 ? "M" : "L"} ${x} ${y}`;
                    }).join(" ")}
                    fill="none"
                    stroke="#0A1128"
                    strokeWidth="3.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  {/* Annotated Crisis Events with Stems, Pins, and Badges */}
                  {HISTORICAL_CRISES.map((c, idx) => {
                    const isSelected = activeCrisisIndex === idx;
                    return (
                      <g key={c.id} className="transition-all duration-300">
                        {/* Vertical Stem from curve to baseline */}
                        <line
                          x1={c.x}
                          y1={c.y}
                          x2={c.x}
                          y2="350"
                          stroke={isSelected ? "#C5A880" : "#CBD5E1"}
                          strokeWidth={isSelected ? "2" : "1"}
                          strokeDasharray={isSelected ? "none" : "3 3"}
                          opacity={isSelected ? "1" : "0.5"}
                        />

                        {/* Guide Stem to Floating Label */}
                        <line
                          x1={c.x}
                          y1={c.y}
                          x2={c.x}
                          y2={c.labelY > c.y ? c.labelY - 10 : c.labelY + 12}
                          stroke={isSelected ? "#0A1128" : "#C5A880"}
                          strokeWidth={isSelected ? "1.5" : "1"}
                          strokeDasharray="2 2"
                          opacity={isSelected ? "1" : "0.7"}
                        />

                        {/* Outer Glow Ring on Selected Pin */}
                        {isSelected && (
                          <circle
                            cx={c.x}
                            cy={c.y}
                            r="11"
                            fill="#C5A880"
                            opacity="0.3"
                            className="animate-pulse"
                          />
                        )}

                        {/* Interactive Crisis Node on Curve */}
                        <circle
                          cx={c.x}
                          cy={c.y}
                          r={isSelected ? "7" : "5"}
                          fill={isSelected ? "#C5A880" : "#0A1128"}
                          stroke="#FFFFFF"
                          strokeWidth="2.5"
                          className="cursor-pointer hover:scale-125 transition-transform"
                          onClick={() => setActiveCrisisIndex(idx)}
                        />

                        {/* Floating Label Badge */}
                        <g
                          className="cursor-pointer hover:opacity-100 transition-opacity"
                          onClick={() => setActiveCrisisIndex(idx)}
                        >
                          <rect
                            x={c.x - 56}
                            y={c.labelY - 12}
                            width="112"
                            height="24"
                            rx="6"
                            fill={isSelected ? "#0A1128" : "#FFFFFF"}
                            stroke={isSelected ? "#C5A880" : "#E2E8F0"}
                            strokeWidth={isSelected ? "1.5" : "1"}
                            filter="drop-shadow(0 1px 2px rgba(0,0,0,0.08))"
                          />
                          <text
                            x={c.x}
                            y={c.labelY}
                            textAnchor="middle"
                            dominantBaseline="central"
                            fontSize="9"
                            fontFamily="monospace"
                            fontWeight={isSelected ? "bold" : "600"}
                            fill={isSelected ? "#C5A880" : "#1E293B"}
                          >
                            {c.short}
                          </text>
                        </g>
                      </g>
                    );
                  })}
                </svg>
              </div>
            </div>

            {/* Interactive Crisis Timeline Slider */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2 text-xs font-mono text-slate-600">
                  <span className="w-2 h-2 rounded-full bg-[#C5A880] animate-pulse" />
                  <span className="font-semibold text-[#0A1128]">Timeline Slider:</span>
                  <span className="text-slate-500">Drag or click to navigate macro shocks (1997 – 2025)</span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setActiveCrisisIndex((prev) => Math.max(0, prev - 1))}
                    disabled={activeCrisisIndex === 0}
                    className="p-1.5 rounded-lg border border-[#E7E2D6] bg-white text-[#0A1128] hover:border-[#C5A880] disabled:opacity-30 disabled:cursor-not-allowed transition-all text-xs font-mono flex items-center gap-1 shadow-2xs"
                    title="Previous Crisis"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Prev</span>
                  </button>
                  <span className="text-xs font-mono font-bold text-[#0A1128] bg-white px-2.5 py-1 rounded-lg border border-[#E7E2D6] shadow-2xs">
                    {HISTORICAL_CRISES[activeCrisisIndex].num} / 08
                  </span>
                  <button
                    onClick={() => setActiveCrisisIndex((prev) => Math.min(HISTORICAL_CRISES.length - 1, prev + 1))}
                    disabled={activeCrisisIndex === HISTORICAL_CRISES.length - 1}
                    className="p-1.5 rounded-lg border border-[#E7E2D6] bg-white text-[#0A1128] hover:border-[#C5A880] disabled:opacity-30 disabled:cursor-not-allowed transition-all text-xs font-mono flex items-center gap-1 shadow-2xs"
                    title="Next Crisis"
                  >
                    <span className="hidden sm:inline">Next</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Slider Track with Custom Progress Fill & Interactive Nodes */}
              <div className="relative py-4 px-2 sm:px-4">
                {/* Visual Track Background */}
                <div className="relative w-full h-3 bg-slate-200 rounded-full overflow-hidden shadow-inner">
                  <div
                    className="h-full bg-gradient-to-r from-[#0A1128] via-[#1E293B] to-[#C5A880] transition-all duration-300 rounded-full"
                    style={{
                      width: `${(activeCrisisIndex / (HISTORICAL_CRISES.length - 1)) * 100}%`,
                    }}
                  />
                </div>

                {/* Range Input (Transparent, sitting above track for effortless drag & touch) */}
                <input
                  type="range"
                  min="0"
                  max={HISTORICAL_CRISES.length - 1}
                  step="1"
                  value={activeCrisisIndex}
                  onChange={(e) => setActiveCrisisIndex(Number(e.target.value))}
                  className="absolute inset-x-2 sm:inset-x-4 top-1/2 -translate-y-1/2 w-[calc(100%-16px)] sm:w-[calc(100%-32px)] h-8 opacity-0 cursor-pointer z-30"
                  aria-label="Timeline Crisis Slider"
                />

                {/* Milestone Node Stops along the Slider */}
                <div className="absolute inset-x-2 sm:inset-x-4 top-1/2 -translate-y-1/2 flex justify-between pointer-events-none z-20">
                  {HISTORICAL_CRISES.map((c, idx) => {
                    const isPassed = idx <= activeCrisisIndex;
                    const isCurrent = idx === activeCrisisIndex;
                    return (
                      <div
                        key={c.id}
                        className="relative flex flex-col items-center pointer-events-auto cursor-pointer"
                        onClick={() => setActiveCrisisIndex(idx)}
                      >
                        {/* Milestone Tick Circle */}
                        <div
                          className={`w-5 h-5 rounded-full border-2 transition-all duration-300 flex items-center justify-center ${
                            isCurrent
                              ? "bg-[#C5A880] border-[#0A1128] ring-4 ring-[#C5A880]/40 scale-125 shadow-md"
                              : isPassed
                              ? "bg-[#0A1128] border-white shadow-xs"
                              : "bg-white border-slate-300"
                          }`}
                        >
                          <div
                            className={`w-1.5 h-1.5 rounded-full ${
                              isCurrent ? "bg-[#0A1128]" : isPassed ? "bg-[#C5A880]" : "bg-slate-300"
                            }`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Milestone Labels Grid Below Slider */}
              <div className="flex justify-between px-1 sm:px-2 pt-2">
                {HISTORICAL_CRISES.map((c, idx) => {
                  const isCurrent = idx === activeCrisisIndex;
                  return (
                    <button
                      key={c.id}
                      onClick={() => setActiveCrisisIndex(idx)}
                      className={`flex flex-col items-center text-center transition-all group ${
                        isCurrent
                          ? "text-[#0A1128] font-bold scale-105"
                          : "text-slate-400 hover:text-slate-700"
                      }`}
                      style={{ width: `${100 / HISTORICAL_CRISES.length}%` }}
                    >
                      <span className={`text-[10px] sm:text-xs font-mono ${isCurrent ? "text-[#C5A880] font-bold" : ""}`}>
                        {c.year.split("–")[0].trim().slice(-2)}'
                      </span>
                      <span className="hidden md:inline-block text-[10px] font-sans truncate max-w-[90px] leading-tight mt-0.5">
                        {c.short.replace(/^\d+[\.\-\s]*/, "").replace(/^\d{4}[\-\s]*/, "")}
                      </span>
                      {isCurrent && (
                        <div className="w-4 h-0.5 bg-[#C5A880] rounded-full mt-1" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active Crisis Spotlight Callout Card */}
            {(() => {
              const activeCrisis = HISTORICAL_CRISES[activeCrisisIndex];
              return (
                <div className="bg-[#FAF8F5] border-2 border-[#C5A880]/70 rounded-2xl p-6 mt-6 shadow-xs transition-all">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-[#E7E2D6] gap-3">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs font-bold px-2.5 py-1 rounded bg-[#0A1128] text-[#C5A880]">
                        Event {activeCrisis.num} // {activeCrisis.year}
                      </span>
                      <h4 className="font-serif-display text-xl sm:text-2xl font-bold text-[#0A1128]">
                        {activeCrisis.name}
                      </h4>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-slate-500">
                        Index Level: <strong className="text-[#0A1128]">{activeCrisis.indexVal}</strong>
                      </span>
                      <span className="text-xs font-mono font-bold px-3 py-1 rounded-full bg-emerald-100 text-emerald-800">
                        ★ {activeCrisis.tag}
                      </span>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-5 mt-4">
                    <div className="bg-white p-4 rounded-xl border border-[#E7E2D6]">
                      <div className="text-[11px] font-mono text-slate-400 uppercase tracking-wider font-bold">
                        Global Shock / Contagion
                      </div>
                      <p className="text-xs text-slate-700 mt-1.5 leading-relaxed">
                        {activeCrisis.impact}
                      </p>
                    </div>

                    <div className="bg-white p-4 rounded-xl border border-[#E7E2D6]">
                      <div className="text-[11px] font-mono text-[#C5A880] uppercase tracking-wider font-bold">
                        Singapore Sovereign Action
                      </div>
                      <p className="text-xs text-slate-700 mt-1.5 leading-relaxed">
                        {activeCrisis.action}
                      </p>
                    </div>

                    <div className="bg-emerald-50/70 p-4 rounded-xl border border-emerald-200">
                      <div className="text-[11px] font-mono text-emerald-800 uppercase tracking-wider font-bold">
                        Empirical Market Outcome
                      </div>
                      <p className="text-xs text-emerald-950 font-medium mt-1.5 leading-relaxed">
                        {activeCrisis.recovery}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Bottom Sovereign Summary Strip */}
            <div className="mt-6 pt-5 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between text-xs text-slate-600 gap-3">
              <div className="flex items-center gap-2 text-[#0A1128] font-semibold">
                <ShieldCheck className="w-4 h-4 text-[#C5A880] shrink-0" />
                <span>
                  Why no structural crash in modern history? High mandatory cash equity + 55% TDSR income caps + S$1T sovereign fortress reserves.
                </span>
              </div>
              <span className="text-[11px] font-mono text-slate-400 shrink-0">Source: Urban Redevelopment Authority (URA) Real Estate Statistics</span>
            </div>
          </div>
        </div>
      </section>

      {/* ==================================================================== */}
      {/* SECTION 4: STRATEGIC ACTION & PRIVATE CONSULTATION                   */}
      {/* ==================================================================== */}
      <section id="contact" className="bg-[#0A1128] text-white py-24 px-6 lg:px-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(#C5A880 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />

        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="text-xs font-mono uppercase text-[#C5A880] font-bold tracking-wider">
                04 // Advisory Mandate
              </span>
              <h2 className="font-serif-display text-3xl sm:text-5xl text-white font-medium mt-2 leading-tight">
                Connect with the Swiss–Singapore Desk.
              </h2>
              <p className="mt-4 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
                Whether you are exploring a prime residential portfolio in Marina Bay or Orchard, or evaluating new launch condo entry with 0% ABSD, speak directly with our partners in Singapore or Switzerland.
              </p>

              {/* Partners Pills */}
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                <div className="bg-[#152243] p-4 rounded-xl border border-white/10">
                  <div className="text-[10px] font-mono text-[#C5A880] uppercase font-bold">Singapore Transactions</div>
                  <div className="font-serif-display text-lg text-white font-medium mt-0.5">Alvan Ng</div>
                  <div className="text-xs text-slate-400 mt-1">NUS M.Arch · CEA Lic. R066487C</div>
                </div>
                <div className="bg-[#152243] p-4 rounded-xl border border-white/10">
                  <div className="text-[10px] font-mono text-[#C5A880] uppercase font-bold">Swiss Investor Relations</div>
                  <div className="font-serif-display text-lg text-white font-medium mt-0.5">Kuru Bhandari</div>
                  <div className="text-xs text-slate-400 mt-1">NUS MSc Real Estate · RICS (Zurich/Geneva)</div>
                </div>
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  href="/#contact"
                  data-testid="advantage-cta-contact"
                  className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0A1128] hover:bg-[#D4C09E] font-bold rounded-lg h-12 px-8 text-sm transition-colors shadow-lg"
                >
                  Request Private Briefing <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/launches"
                  className="inline-flex items-center gap-2 bg-[#152243] text-white hover:bg-[#1C2D5A] border border-white/10 rounded-lg h-12 px-6 text-sm font-medium transition-colors"
                >
                  Explore New Launch Catalog <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Checklist Box */}
            <div className="lg:col-span-5 bg-white/[0.04] border border-white/15 rounded-3xl p-8 backdrop-blur-md">
              <h3 className="font-serif-display text-xl text-white font-medium pb-4 border-b border-white/10">
                What We Handle for You
              </h3>
              <ul className="mt-5 space-y-3.5 text-xs text-slate-300">
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-[#C5A880] shrink-0 mt-0.5" />
                  <span><strong>EFTA Treaty Verification:</strong> IRAS 0% ABSD confirmation with Singapore conveyancing counsel.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-[#C5A880] shrink-0 mt-0.5" />
                  <span><strong>Developer Allocations:</strong> Accessing units before public balloting or sell-outs.</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-[#C5A880] shrink-0 mt-0.5" />
                  <span><strong>Turnkey Asset Management:</strong> Complete post-handover leasing, tenant curation, and asset reporting.</span>
                </li>
              </ul>

              <div className="mt-8 pt-5 border-t border-white/10 flex items-center justify-between text-xs text-slate-400">
                <span>Direct Inquiries:</span>
                <a href="mailto:partner@alpinestraitspartners.com.sg" className="text-[#C5A880] hover:underline font-mono">
                  partner@alpinestraitspartners.com.sg
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================================================================== */}
      {/* FOOTER                                                               */}
      {/* ==================================================================== */}
      <footer className="bg-[#070C1D] border-t border-white/10 text-slate-400 py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-xs">
          <div className="flex items-center gap-3">
            <Link to="/" className="font-serif-display text-white text-base font-semibold">
              Alpine Straits Property Partners
            </Link>
            <span className="text-slate-600">|</span>
            <span className="text-slate-400">Singapore · Zurich</span>
          </div>

          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/launches" className="hover:text-white transition-colors">New Launches</Link>
            <Link to="/advantage" className="text-[#C5A880] font-medium">The Advantage</Link>
            <a href="/#about" className="hover:text-white transition-colors">About</a>
            <a href="/#contact" className="hover:text-white transition-colors">Contact</a>
          </div>

          <div className="text-slate-500 text-[11px]">
            © {new Date().getFullYear()} Alpine Straits Property Partners. Prepared under EFTA-SG Article 10.5 framework.
          </div>
        </div>
      </footer>
    </div>
  );
}
